import {useMemo} from "react"
import {useHistory, useLocation} from "react-router-dom"
import {HistoryParamValue, pushUrlHistoryParams} from "../lib/routing"

/**
 * Handle a group of search params
 * @param paramNames - names of parameters to handle
 * @returns an array, where array[0] is an object with values and array[1] is a "set parameters" method
 * @example
 * const [{school: schoolValue, district: districtValue}, setData] = useSearchParamsGroup("school", "district")
 * setData({school: "1234", district: "321"})
 */
export const useSearchParamsGroup: (
    ...paramNames: Array<string>
) => [
    {[paramName: string]: string | undefined},
    (params: {[paramName: string]: HistoryParamValue}) => void
] = (...paramNames) => {
    const history = useHistory()
    const {search} = useLocation()

    const valuesInitial = paramNames.reduce((res, name) => ({...res, [name]: null}), {})

    const values = useMemo(() => {
        const urlParams = new URLSearchParams(search)
        return paramNames.reduce(
            (values, paramName) => ({
                ...values,
                [paramName]: urlParams.get(paramName) || undefined,
            }),
            valuesInitial
        )
    }, [search, paramNames, valuesInitial])

    return useMemo(() => [values, pushUrlHistoryParams(history)], [history, values])
}
